import { Outlet, createFileRoute } from '@tanstack/react-router';

import { PillNav } from '@/components/PillNav/PillNav';
import { useSplatPathSegments } from '@/hooks/useSplatPathSegments';
import { useUser } from '@/hooks/useUser';

// Pathless layout (per `_public.tsx` precedent) — provides the shared Access
// Control header (H1 + description + PillNav) above Users / Teams / Sources
// child routes. The `_access` segment is stripped from URLs, so the children
// still resolve at /$org/settings/users, /teams, /sources.
export const Route = createFileRoute('/$organizationName/settings/_access')({
  component: AccessLayout,
});

type AccessItem = {
  key: 'sources' | 'teams' | 'users';
  label: 'Sources' | 'Teams' | 'Users';
  to: '/$organizationName/settings/sources' | '/$organizationName/settings/teams' | '/$organizationName/settings/users';
};

function AccessLayout() {
  const { isSourceAdmin, isSuperAdmin, routeOrganizationName } = useUser();

  // Which Access child is currently rendered — drives the PillNav active state.
  const pathSegments = useSplatPathSegments(Route);
  const activeAccessChild = pathSegments[0]?.toLowerCase();

  const items: AccessItem[] = [];
  if (isSuperAdmin || isSourceAdmin) {
    items.push({ key: 'sources', label: 'Sources', to: '/$organizationName/settings/sources' });
  }
  items.push({ key: 'teams', label: 'Teams', to: '/$organizationName/settings/teams' });
  if (isSuperAdmin) {
    items.push({ key: 'users', label: 'Users', to: '/$organizationName/settings/users' });
  }

  return (
    <div className="px-6 pt-6">
      <div className="mb-6 flex flex-col items-start space-y-1">
        <h1 className="text-primary text-xl font-medium">Access Control</h1>
        <p className="text-muted-foreground">Manage permissions across your organization.</p>
      </div>
      <PillNav
        aria-label="Access Control sections"
        activeKey={activeAccessChild}
        items={items.map((item) => ({
          key: item.key,
          label: item.label,
          to: item.to,
          params: { organizationName: routeOrganizationName },
        }))}
      />
      <Outlet />
    </div>
  );
}
